/**
 * Work logs list state in the URL (T-40, PRD §4.11): the view, the period,
 * the filters behind "סינון" and the review queue live in `?…`, so Back /
 * Forward restore them and a copied link reproduces the list. The links other
 * screens already use keep working: `?month=2026-09` (dashboard widget),
 * `?review=pending&from=home` (Home "שעות לאישור"), `?create=true` (customer
 * page; a one-shot flag the page consumes). Pure, unit-tested
 * (`src/utils/__tests__/workLogsListParams.test.ts`).
 */
import { canonicalQuery, type ListSchema, type ListValues } from '../table/listParams';
import {
  EMPTY_WORKLOG_FILTERS, monthParamRange, periodRange, presetForRange,
  type WorkLogFilters, type WorkLogReview, type WorkLogType,
} from './workLogsFiltersModel';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DAY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const HOUR_MINUTE = /^([01]\d|2[0-3]):[0-5]\d$/;
/** `me` (the signed-in worker) or one employee's id (the "תיעוד הצוות" widget link). */
const AGENT = /^(me|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/** The `agent_id` the API gets for `?agent=`: the signed-in worker for `me`, the id itself otherwise. */
export function agentIdOf(agent: string, userId: string | undefined): string {
  if (agent === 'me') return userId ?? '';
  return UUID.test(agent) ? agent : '';
}

export const RELATIVE_PERIODS = ['today', 'week', 'month', 'lastMonth'] as const;
export type RelativePeriod = (typeof RELATIVE_PERIODS)[number];

export const WORKLOG_PARAMS = {
  /**
   * The segmented "רישומים · מחזורי עבודה". P-10d (D-79 §3): absent =
   * מחזורי עבודה (the default); `logs` = רישומים; old `records` links still work.
   */
  view: { kind: 'enum', values: ['logs', 'records'] },
  /**
   * SEC-2b (D-104) — the role tabs: absent = שלי; `others` (manager / AM / PM /
   * TL), `open` (manager / employee), `active` (manager).
   */
  tab: { kind: 'enum', values: ['mine', 'others', 'open', 'active'] },
  /** Status of the work-cycles view. */
  rstatus: { kind: 'enum', values: ['open', 'closed'] },
  /** A period relative to today, so a saved view "החודש" stays "this month". */
  period: { kind: 'enum', values: RELATIVE_PERIODS },
  /** A whole calendar month (the dashboard link). */
  month: { kind: 'string', pattern: MONTH },
  /** A custom range (inclusive local days). */
  start: { kind: 'string', pattern: DAY },
  end: { kind: 'string', pattern: DAY },
  type: { kind: 'enum', values: ['tickets', 'tasks', 'milestones', 'replies'] },
  project: { kind: 'string', pattern: UUID },
  /** The task's engagement (P-02b §6.7: service / hour-bank hours have no project). */
  engagement: { kind: 'string', pattern: UUID },
  customer: { kind: 'string', pattern: UUID },
  /** SEC-2b (D-104) — the customer (organization) of the "של אחרים" views. */
  org: { kind: 'string', pattern: UUID },
  tfrom: { kind: 'string', pattern: HOUR_MINUTE },
  tto: { kind: 'string', pattern: HOUR_MINUTE },
  /** One worker's logs (`agent_id`): `me` = the signed-in worker, or an employee id. */
  agent: { kind: 'string', pattern: AGENT },
  /** Opened from a Home chip ("מסונן מדף הבית"). */
  from: { kind: 'enum', values: ['home'] },
  /** P-06/E (D-62) — "כל הלוגים של המשימה": one task's logs (P-10d: also the "משימה" filter). */
  ticket: { kind: 'string', pattern: UUID },
  /** P-10d (D-72) — the "אבן דרך" filter. */
  milestone: { kind: 'string', pattern: UUID },
  /**
   * FIX-B9 (D-161, D-168) — the review queue the cube "דורש טיפול עכשיו" opens:
   * `manual` = "לוגים שלא אושרו", `auto` = "רשומות אוטומטיות לבדיקה" (OTE manager
   * only). Server-side, the same rule as the counts. `pending` = every log waiting for a review
   * (API / old links only - P-30c, QX-115 removed the "שעות לאישור" chip and the team band's link).
   */
  review: { kind: 'enum', values: ['pending', 'manual', 'auto'] },
} satisfies ListSchema;

export type WorkLogParams = ListValues<typeof WORKLOG_PARAMS>;

/**
 * Every key that narrows the list. (P-10d, D-83: the "תצוגות" menu was removed;
 * the segmented view and the tab stay out of it. D-72: "ממתין לאישור" removed.)
 */
export const WORKLOG_VIEW_KEYS = [
  'period', 'month', 'start', 'end', 'type', 'project', 'engagement', 'customer', 'org', 'tfrom', 'tto', 'agent', 'ticket', 'milestone', 'review',
] as const;

/** Every key that narrows the entries list (for "ניקוי הכול" from the empty state). */
export const WORKLOG_FILTER_KEYS = WORKLOG_VIEW_KEYS;

export const workLogViewQuery = (v: Partial<Record<(typeof WORKLOG_VIEW_KEYS)[number], string>>) =>
  canonicalQuery(WORKLOG_PARAMS, v as Partial<WorkLogParams>, WORKLOG_VIEW_KEYS);

/** The date range the URL describes: a custom range wins, then a month, then a relative period. */
export function rangeFromParams(v: Pick<WorkLogParams, 'period' | 'month' | 'start' | 'end'>, today: Date): { from: string; to: string } {
  if (v.start || v.end) return { from: v.start, to: v.end };
  const month = monthParamRange(v.month || null);
  if (month) return month;
  if (v.period) return periodRange(v.period as RelativePeriod, today);
  return { from: '', to: '' };
}

/**
 * The URL patch for a range chosen in the period control: a preset becomes
 * `period=` (relative, so it survives in a saved view), a whole calendar month
 * `month=`, anything else `start=` / `end=`. The other period keys are cleared.
 */
export function periodPatch(range: { from: string; to: string }, today: Date): Pick<WorkLogParams, 'period' | 'month' | 'start' | 'end'> {
  const none = { period: '', month: '', start: '', end: '' };
  const preset = presetForRange(range.from, range.to, today);
  if (preset === 'all') return none;
  if (preset !== 'custom') return { ...none, period: preset };
  if (range.from && range.to && range.from.slice(0, 7) === range.to.slice(0, 7)) {
    const whole = monthParamRange(range.from.slice(0, 7));
    if (whole && whole.from === range.from && whole.to === range.to) return { ...none, month: range.from.slice(0, 7) };
  }
  return { ...none, start: range.from, end: range.to };
}

const TYPE_OF: Record<string, WorkLogType> = { tickets: 'tickets', tasks: 'tasks', milestones: 'milestones', replies: 'replies' };

/** The filters the API call is built from (`workLogQueryParams`), read from the URL. */
export function filtersFromParams(v: WorkLogParams, today: Date, userId: string | undefined): WorkLogFilters {
  return {
    ...EMPTY_WORKLOG_FILTERS,
    ...rangeFromParams(v, today),
    type: TYPE_OF[v.type] ?? 'all',
    projectId: v.project,
    engagementId: v.engagement,
    customerId: v.customer,
    organizationId: v.org,
    timeFrom: v.tfrom,
    timeTo: v.tto,
    agentId: agentIdOf(v.agent, userId),
    ticketId: v.ticket,
    milestoneId: v.milestone,
    review: v.review as WorkLogReview,
  };
}

/** A change from the "סינון" panel as a URL patch (type, project, engagement, customer, task, milestone, time of day). */
export function filterPatch(patch: Partial<WorkLogFilters>): Partial<WorkLogParams> {
  const out: Partial<WorkLogParams> = {};
  if (patch.type !== undefined) out.type = patch.type === 'all' ? '' : patch.type;
  if (patch.projectId !== undefined) out.project = patch.projectId;
  if (patch.engagementId !== undefined) out.engagement = patch.engagementId;
  if (patch.customerId !== undefined) out.customer = patch.customerId;
  if (patch.organizationId !== undefined) out.org = patch.organizationId;
  if (patch.timeFrom !== undefined) out.tfrom = patch.timeFrom;
  if (patch.timeTo !== undefined) out.tto = patch.timeTo;
  if (patch.ticketId !== undefined) out.ticket = patch.ticketId;
  if (patch.milestoneId !== undefined) out.milestone = patch.milestoneId;
  return out;
}

/** A review-queue row: it needs a review and nobody reviewed it yet. */
export function isPendingReview(log: { requires_review?: boolean | null; reviewed?: boolean | null }): boolean {
  return !!log.requires_review && !log.reviewed;
}

/** "2:05" style hours:minutes for durations of an hour or more; minutes below that. */
export function durationParts(totalMinutes: number): { hours: number; minutes: number; padded: string } {
  const m = Math.max(0, Math.round(Number(totalMinutes) || 0));
  const hours = Math.floor(m / 60);
  const minutes = m % 60;
  return { hours, minutes, padded: String(minutes).padStart(2, '0') };
}
